import type { ColumnType } from 'antd/es/table';

import { sortByAmount, sortByTime } from 'utils/antd/sorters';
import { formatDecimalAmount } from 'utils/formatters/formatCurrency';

import type { WithdrawalRequests } from 'api';

import { withdrawalRequestStatuses } from 'models/withdrawal';

import type { FindAccountByAccountNumber } from 'hooks/context/useVirtualAccounts';

import { ColumnTitle } from '@atoms/ColumnTitle';
import { WithdrawalRequestsIcon } from '@atoms/Icon/WithdrawalRequestsIcon';
import { StatusTag } from '@atoms/StatusTag';

import { FormattedDateTime } from '@molecules/FormattedDateTime';

export const getColumns = (
  findAccountByAccountNumber: FindAccountByAccountNumber
): ColumnType<WithdrawalRequests>[] => {
  return [
    {
      dataIndex: 'icon',
      key: 'icon',
      width: 100,
      render: () => (
        <div className="flex justify-center">
          <WithdrawalRequestsIcon />
        </div>
      )
    },
    {
      title: <ColumnTitle title="Date" />,
      dataIndex: 'created_at',
      key: 'created_at',
      render: (createdAt: string) => <FormattedDateTime value={createdAt} />,
      sorter: (a, b) => sortByTime(a.created_at, b.created_at),
      onHeaderCell: () => ({
        className: 'align-sorter-icon'
      })
    },
    {
      title: <ColumnTitle title="ID/Reference" />,
      dataIndex: 'reference',
      key: 'reference',
      width: 200,
      render: (reference: string, record: WithdrawalRequests) => (
        <div>
          {record.external_reference}
          <div className="font-semibold">{reference.toLocaleUpperCase()}</div>
        </div>
      ),
      onHeaderCell: () => ({
        className: 'align-sorter-icon'
      })
    },
    {
      title: <ColumnTitle title="Account" />,
      dataIndex: 'debtor_account_number',
      key: 'debtor_account_number',
      align: 'right',
      render: (debtorAccountNumber) => {
        const account = findAccountByAccountNumber(debtorAccountNumber);
        return (
          <div>
            {debtorAccountNumber}
            <div className="font-semibold">{account?.name}</div>
          </div>
        );
      }
    },
    {
      title: <ColumnTitle title="Amount" />,
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      sorter: (a, b) => sortByAmount(a.amount, b.amount),
      render: (amount, record: WithdrawalRequests) => {
        const formattedAmount = formatDecimalAmount(amount);
        return (
          <div>
            {record.currency_code}
            <div className="font-semibold">{formattedAmount}</div>
          </div>
        );
      }
    },
    {
      title: <ColumnTitle title="Status" />,
      dataIndex: 'status',
      key: 'status',
      align: 'right',
      render: (status: string) => {
        return (
          <StatusTag status={status} statuses={withdrawalRequestStatuses} />
        );
      }
    }
  ];
};
